/* eslint-disable */
import moment from 'moment';
import { Calendar } from 'lucide-react';
import { useMemo } from 'react';

const parseValue = (valueStr) => {
	if (!valueStr || typeof valueStr !== 'string') return { measured: 0, planned: 0, unit: '' };

	const match = valueStr.match(/^([\d,]+(?:\.\d+)?)\s*\/\s*([\d,]+(?:\.\d+)?)\s*(.*)?$/);
	if (!match) {
		const singleMatch = valueStr.match(/^([\d,]+(?:\.\d+)?)\s*(.*)?$/);
		if (singleMatch) {
			return {
				measured: parseFloat(singleMatch[1].replace(/,/g, '')) || 0,
				planned: 0,
				unit: (singleMatch[2] || '').trim(),
			};
		}
		return { measured: 0, planned: 0, unit: '' };
	}

	return {
		measured: parseFloat(match[1].replace(/,/g, '')) || 0,
		planned: parseFloat(match[2].replace(/,/g, '')) || 0,
		unit: (match[3] || '').trim(),
	};
};

const SectionRow = ({ label, measured, planned, unit }) => {
	const percentage = planned > 0 ? Math.round((measured / planned) * 100) : 0;

	// Dynamic color logic based on percentage
	const getProgressColor = (pct) => {
		if (pct < 40) return 'bg-red-500';
		if (pct < 70) return 'bg-amber-500';
		return 'bg-emerald-500';
	};

	const getTextColor = (pct) => {
		if (pct < 40) return 'text-red-600';
		if (pct < 70) return 'text-amber-600';
		return 'text-emerald-600';
	};

	const progressColor = getProgressColor(percentage);
	const textColor = getTextColor(percentage);

	return (
		<div className='flex items-center px-8 py-5 border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition-colors'>
			{/* Section Name */}
			<div className='flex-1 min-w-0 pr-4 text-start'>
				<span className='text-sm font-bold text-slate-700 block truncate'>
					{label}
				</span>
			</div>

			{/* Progress Bar Container */}
			<div className='flex-[1.5] px-6'>
				<div className='h-3 w-full bg-slate-100 rounded-full overflow-hidden shadow-inner'>
					<div
						className={`h-full ${progressColor} rounded-full transition-all duration-700 ease-out shadow-sm`}
						style={{ width: `${Math.min(percentage, 100)}%` }}
					/>
				</div>
			</div>

			{/* Value & Info */}
			<div className='w-[180px] flex flex-col items-end shrink-0 pl-4'>
				<div className={`flex items-baseline gap-1.5 ${textColor}`}>
					<span className='text-xl font-black tracking-tight leading-none'>
						{measured.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
					</span>
					<span className='text-xs font-bold opacity-80 uppercase tracking-wide'>
						{unit}
					</span>
				</div>
				<div className='flex items-center gap-1.5 mt-1.5'>
					<span className='text-[10px] font-extrabold text-slate-400 uppercase tracking-widest leading-none'>
						{percentage}%
					</span>
					<span className='text-[10px] font-bold text-slate-300'>|</span>
					<span className='text-[10px] font-extrabold text-slate-400 uppercase tracking-widest leading-none'>
						Plan: {planned.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}
					</span>
				</div>
			</div>
		</div>
	);
};

const MeasuredProductionModalContent = ({ rawData, dateRange }) => {

	const stats = rawData?.columns || [];

	const excludeKeys = ['Total', 'Product Name', 'EGA'];

	// Format dates for display
	const fromDate = dateRange?.from ? moment(dateRange.from).format('DD-MM-YYYY') : '';
	const toDate = dateRange?.to ? moment(dateRange.to).format('DD-MM-YYYY') : '';

	// Process and sort sections
	const sections = useMemo(() => {
		return stats
			.filter(item => !excludeKeys.some(k => item.label.includes(k)))
			.map(item => {
				const totalEntry = item.data?.find(d => d.label === 'Total') || item.data?.[0];
				const { measured, planned, unit } = parseValue(totalEntry?.value);
				return {
					...item,
					measured,
					planned,
					unit,
					percentage: planned > 0 ? (measured / planned) * 100 : 0
				};
			})
			.filter(s => s.planned > 0 || s.measured > 0)
			.sort((a, b) => a.percentage - b.percentage);
	}, [stats]);

	return (
		<div className='flex flex-col bg-white uppercase overflow-hidden'>
			{/* Header */}
			<header className='flex justify-between items-center px-8 py-4 border-b border-slate-100 bg-white grow-0 shrink-0 z-10'>
				<h2 className='text-xl font-black text-slate-800 tracking-tight'>
					Section Wise Production
				</h2>
				<div className='flex items-center gap-2.5 px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl shadow-sm'>
					<Calendar size={16} />
					<span className='text-xs font-bold text-slate-600 tracking-wide'>
						{fromDate} ~ {toDate}
					</span>
				</div>
			</header>

			{/* Content Area */}
			<main className='p-0 bg-slate-50 overflow-hidden'>
				<div className='bg-white max-h-[calc(100vh-340px)] overflow-y-auto p-4'>
					{sections.length === 0 ? (
						<div className='flex flex-col items-center justify-center p-20 text-center'>
							<div className='w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4'>
								<Calendar size={24} className='text-slate-300' />
							</div>
							<p className='text-slate-400 font-bold text-sm uppercase tracking-widest'>
								No Production Data Found
							</p>
						</div>
					) : (
						sections.map((section, idx) => (
							<SectionRow
								key={section.key || idx}
								label={section.label}
								measured={section.measured}
								planned={section.planned}
								unit={section.unit}
							/>
						))
					)}
				</div>
			</main>
		</div>
	);
};

export default MeasuredProductionModalContent;
